import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import Logo from 'src/components/Logo'
import { useSocialMedia } from 'src/hooks/socialMedia'
import { getLocalizedRecordData } from 'src/i18n/localizedContent'
import { localeAtom } from 'src/stores/locale'
import { config } from 'src/utils/config'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'

export interface FooterLink {
  title: string
  trackId: string
  to: string
  external?: boolean
}

const TSUBAKI_SUPPORTED_LOCALES = new Set(['en', 'ja', 'zh', 'ko'])
const LOCALE_ALIAS: Record<string, string> = { kr: 'ko' }

const MIO_BLOG_URLS = {
  en: 'https://blog.pixai.art/en/meet-mio-pixais-official-mascot/',
  ja: 'https://blog.pixai.art/ja/meet-mio-pixais-official-mascot-ja/',
  ko: 'https://blog.pixai.art/ko/meet-mio-pixais-official-mascot-ko/',
  zh: 'https://blog.pixai.art/zh/meet-mio-pixais-official-mascot-zh/',
}

export type AppFooterProps = StyleProps

export default function AppFooter({ className, style }: AppFooterProps) {
  const { t, i18n } = useTranslation(['common'])
  const locale = useAtomValue(localeAtom)

  const tsubakiUrl = useMemo(() => {
    const raw = i18n.language.slice(0, 2)
    const lang = LOCALE_ALIAS[raw] ?? raw
    const prefix = TSUBAKI_SUPPORTED_LOCALES.has(lang) ? lang : 'en'
    return `https://pixai.art/${prefix}/tsubaki-2`
  }, [i18n.language])

  const mioBlogUrl = getLocalizedRecordData(locale, MIO_BLOG_URLS)!

  const links: FooterLink[] = useMemo(() => {
    return [
      {
        title: t('common:mio.label'),
        trackId: 'footer_mio_click',
        to: mioBlogUrl,
        external: true,
      },
      {
        title: t('common:tags.page.title'),
        trackId: 'footer_tags_click',
        to: '/tags',
      },
      {
        title: t('common:help-center.link.label'),
        trackId: 'footer_how_to_use_click',
        to: t('common:help-center.link.href') || config.helpCenterUrl,
        external: true,
      },
      {
        title: t('common:content-rules.link.label'),
        trackId: 'footer_content_rules_click',
        to: t('common:content-rules.link.href'),
        external: true,
      },
      {
        title: t('common:contact'),
        trackId: 'footer_feedback_click',
        to: '/feedback',
      },
      {
        title: t('common:privacy-policy'),
        trackId: 'footer_policy_click',
        to: '/privacy',
      },
      {
        title: t('common:copyright-policy'),
        trackId: 'footer_copyright_click',
        to: '/copyright',
      },
      { title: t('common:tos'), trackId: 'footer_terms_click', to: '/terms' },
      {
        title: t('common:legal-information'),
        trackId: 'footer_legal_information_click',
        to: '/legal',
      },
    ]
  }, [t, mioBlogUrl])

  const socialMedias = useSocialMedia()

  return (
    <footer
      className={cx(
        'px-4 msm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-sm text-primary-light bg-background border-t border-black/10 dark:border-white/10',
        'max-mmd:mb-[60px]',
        className,
      )}
      style={style}
    >
      <div className="w-full max-w-screen-2xl mx-auto flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <Logo className="size-6" />
          <span className="text-primary-lighter">
            &copy; {new Date().getFullYear()} PixAI
          </span>
        </div>
        <div className="flex items-center gap-2">
          {socialMedias.map(it => (
            <a
              key={it.key}
              href={it.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={it.name}
            >
              <it.icon className="size-5" />
            </a>
          ))}
        </div>
        <a
          href={tsubakiUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            track('footer_tsubaki_click')
          }}
        >
          Tsubaki.2
        </a>
        {links.map(link => (
          <Link
            key={link.trackId}
            to={link.to}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noopener noreferrer' : undefined}
            onClick={() => {
              track(link.trackId)
            }}
          >
            {link.title}
          </Link>
        ))}
      </div>
    </footer>
  )
}
